import TagManager from 'react-gtm-module'

import { CampaignPlatformTypeEnum, CampaignTypeEnum } from 'src/Types/campaigns'
import { PaymentMethodEnum, TagManagerEnum } from 'src/Constants/enums'
import { PAYPAL_SAVED } from 'src/Constants/constants'

import { EventNameEnum, EventTypeEnum } from '../tagManagerEnums'

export const checkoutViewEvent = ({
  campaignType,
  genreList,
  campaignId,
  resultPrice,
  platformName,
  userId,
  campaignNumber,
  paymentMethod,
  subscriptionPlan,
}: {
  campaignType: string
  genreList: string
  campaignId: number | string
  resultPrice: number
  userId?: number | string
  campaignNumber?: number
  platformName: string
  paymentMethod: PaymentMethodEnum

  subscriptionPlan?: string
}): void => {
  TagManager.dataLayer({
    dataLayer: {
      event: EventTypeEnum.CheckoutView,
      event_name: EventNameEnum.CheckoutView,
      'campaign-type': campaignType,
      'campaign-platform': platformName,
      'campaign-number':
        campaignType === CampaignTypeEnum.FIRST ? 1 : campaignNumber,
      'campaign-id': campaignId,
      ...(userId ? { userID: userId } : {}),
      'genre-list': genreList,
      'result-price': resultPrice,
      payment_method:
        paymentMethod === PaymentMethodEnum.PAYPAL_BILLING_AGREEMENT
          ? PAYPAL_SAVED
          : paymentMethod,
      ...(subscriptionPlan ? { subscription_plan: subscriptionPlan } : {}),
    },
  })
}

export const checkoutConfirmedPayment = ({
  platformName,
  campaignType,
  campaignNumber,
  campaignId,
  userId,
  resultPrice,
  genreList,
  paymentMethod,
  isRememberPayPalForFuture,
  subscriptionPlan,
}: {
  platformName: string
  campaignType: string
  campaignNumber: number
  campaignId: number | string
  userId: number | string
  resultPrice: number
  genreList?: string
  paymentMethod: PaymentMethodEnum | string
  isRememberPayPalForFuture?: boolean
  subscriptionPlan?: string
}): void => {
  TagManager.dataLayer({
    dataLayer: {
      event: EventTypeEnum.CheckoutConfirmedPayment,
      event_name: EventNameEnum.CheckoutConfirmedPayment,
      'campaign-type': campaignType,
      'campaign-platform': platformName,
      'campaign-number':
        campaignType === CampaignTypeEnum.FIRST ? 1 : campaignNumber,
      'campaign-id': campaignId,
      ...(userId ? { userID: userId } : {}),
      ...(resultPrice ? { 'result-price': resultPrice } : {}),
      'genre-list': genreList,
      payment_method:
        campaignType === 'first' &&
        paymentMethod === PaymentMethodEnum.PAYPAL &&
        isRememberPayPalForFuture
          ? PAYPAL_SAVED
          : paymentMethod,
      ...(subscriptionPlan ? { subscription_plan: subscriptionPlan } : {}),
    },
  })
}

export const campaignBudgetUpdateEvent = ({
  campaignType,
  platformName,
  campaignNumber,
  campaignId,
  userId,
  resultPrice,
  genreList,
  subscriptionPlan,
}: {
  campaignType: string
  platformName: string
  campaignNumber: number
  campaignId: number
  userId: number | string
  resultPrice: number
  genreList: string
  subscriptionPlan?: string
}): void => {
  TagManager.dataLayer({
    dataLayer: {
      event: EventTypeEnum.CampaignBudgetUpdateEvent,
      event_name: EventNameEnum.CampaignBudgetUpdateEvent,
      'campaign-type': campaignType,
      'campaign-platform': platformName,
      'campaign-number':
        campaignType === CampaignTypeEnum.FIRST ? 1 : campaignNumber,
      'campaign-id': campaignId,
      ...(userId ? { userID: userId } : {}),
      'result-price': resultPrice,
      'genre-list': genreList,
      ...(subscriptionPlan ? { subscription_plan: subscriptionPlan } : {}),
    },
  })
}

export const campaignBudgetReceived = ({
  campaignType,
  platformName,
  campaignNumber,
  campaignId,
  userId,
  resultPrice,
  genreList,
  isAnyLanguage,
  subscriptionPlan,
}: {
  campaignType: string
  platformName: string
  campaignNumber: number
  campaignId: number
  userId: number | string
  resultPrice: number
  genreList: string
  isAnyLanguage?: boolean
  subscriptionPlan?: string
}): void => {
  TagManager.dataLayer({
    dataLayer: {
      event: EventTypeEnum.CampaignBudgetReceived,
      event_name: EventNameEnum.CampaignBudgetReceived,
      'campaign-type': campaignType,
      'campaign-platform': platformName,
      'campaign-number':
        campaignType === CampaignTypeEnum.FIRST ? 1 : campaignNumber,
      'campaign-id': campaignId,
      ...(userId ? { userID: userId } : {}),
      'result-price': resultPrice,
      'genre-list': genreList,
      ...(platformName === CampaignPlatformTypeEnum.SPOTIFY
        ? {
            language_options: isAnyLanguage
              ? TagManagerEnum.WIDER_LANGUAGES_MATCHING
              : TagManagerEnum.SELECTED_LANGUAGES_ONLY,
          }
        : {}),
      ...(subscriptionPlan ? { subscription_plan: subscriptionPlan } : {}),
    },
  })
}

type ThresholdCouponEventParams = {
  campaignId: number | string
  platformName: string
  userId?: number | string
  couponCode: string
  minSpend: number
  orderTotal: number
  remaining: number
}

type ThresholdCouponRaisedEventParams = ThresholdCouponEventParams & {
  orderTotalBefore: number
  orderTotalAfter: number
}

const pushThresholdCouponEvent = (
  event: EventTypeEnum,
  eventName: EventNameEnum,
  {
    campaignId,
    platformName,
    userId,
    couponCode,
    minSpend,
    orderTotal,
    remaining,
  }: ThresholdCouponEventParams,
  extra: Record<string, number> = {},
): void => {
  TagManager.dataLayer({
    dataLayer: {
      event,
      event_name: eventName,
      'campaign-id': campaignId,
      'campaign-platform': platformName,
      ...(userId ? { userID: userId } : {}),
      coupon_code: couponCode,
      min_spend: minSpend,
      order_total: orderTotal,
      remaining,
      ...extra,
    },
  })
}

export const thresholdCouponOfferShownEvent = (
  params: ThresholdCouponEventParams,
): void =>
  pushThresholdCouponEvent(
    EventTypeEnum.ThresholdCouponOfferShown,
    EventNameEnum.ThresholdCouponOfferShown,
    params,
  )

export const thresholdCouponOfferAppliedEvent = (
  params: ThresholdCouponEventParams,
): void =>
  pushThresholdCouponEvent(
    EventTypeEnum.ThresholdCouponOfferApplied,
    EventNameEnum.ThresholdCouponOfferApplied,
    params,
  )

export const thresholdCouponOfferDismissedEvent = (
  params: ThresholdCouponEventParams,
): void =>
  pushThresholdCouponEvent(
    EventTypeEnum.ThresholdCouponOfferDismissed,
    EventNameEnum.ThresholdCouponOfferDismissed,
    params,
  )

export const thresholdCouponRaisedEvent = ({
  orderTotalBefore,
  orderTotalAfter,
  ...params
}: ThresholdCouponRaisedEventParams): void =>
  pushThresholdCouponEvent(
    EventTypeEnum.ThresholdCouponRaised,
    EventNameEnum.ThresholdCouponRaised,
    params,
    {
      order_total_before: orderTotalBefore,
      order_total_after: orderTotalAfter,
    },
  )

export const thresholdCouponUnlockedEvent = (
  params: ThresholdCouponEventParams,
): void =>
  pushThresholdCouponEvent(
    EventTypeEnum.ThresholdCouponUnlocked,
    EventNameEnum.ThresholdCouponUnlocked,
    params,
  )

export const thresholdCouponRemovedEvent = (
  params: ThresholdCouponEventParams,
): void =>
  pushThresholdCouponEvent(
    EventTypeEnum.ThresholdCouponRemoved,
    EventNameEnum.ThresholdCouponRemoved,
    params,
  )
